import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ref, set, onValue } from 'firebase/database';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DrawerPanel } from '../ui/DrawerPanel';
import { database } from '../../lib/firebase';
import { SeniorLocation } from '../../hooks/useSeniors';
import { useSeniorLocation } from '../../hooks/useSeniorLocation';
import { FEATURES } from '../../constants/featureFlags';

// Fix default Leaflet marker icon (broken in webpack builds)
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface GPSZoneDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  seniorName: string;
  location?: SeniorLocation;
}

interface GeofenceConfig {
  enabled: boolean;
  homeLat: number;
  homeLng: number;
  radiusMeters: number;
}

// Spec: AidFone_Johnley_Briefing_March2026.docx §2
// Alert radius selector: 500m / 1km / 2km / 5km
//
// ⚠️ Behind FEATURES.GEOFENCING_ENABLED — see the render block below. Kept for
// the Wave 1.5 rebuild; unreferenced while the flag is off.
const RADIUS_PRESETS = [
  { value: 500, label: '500m' },
  { value: 1000, label: '1km' },
  { value: 2000, label: '2km' },
  { value: 5000, label: '5km' },
];

function formatLocationTime(timestamp: number): string {
  const now = Date.now() / 1000;
  const diffSec = Math.floor(now - timestamp);
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  return `${diffDays}d ago`;
}

const MapAutoCenter: FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (lat !== 0 && lng !== 0) {
      map.setView([lat, lng], 15, { animate: true });
    }
  }, [lat, lng, map]);
  return null;
};

export const GPSZoneDrawer: FC<GPSZoneDrawerProps> = ({
  isOpen,
  onClose,
  seniorId,
  seniorName,
  location: legacyLocation,
}) => {
  const { t } = useTranslation();
  // F03 senior_location is the authoritative source — the legacy `location`
  // prop comes from useSeniors' Firebase RTDB path which is only fed by old
  // emulator runs and can be days stale. Prefer the Supabase row when
  // present; fall back to the prop only when senior_location is empty (early
  // dashboards before the first F03 write).
  const { location: freshLocation } = useSeniorLocation(isOpen ? seniorId : undefined);
  const location: SeniorLocation | undefined = freshLocation
    ? {
        lat: freshLocation.lat,
        lng: freshLocation.lon,
        accuracy: freshLocation.accuracy_meters,
        timestamp: Math.floor(new Date(freshLocation.updated_at).getTime() / 1000),
      }
    : legacyLocation;

  const [config, setConfig] = useState<GeofenceConfig>({
    enabled: false,
    homeLat: location?.lat || 0,
    homeLng: location?.lng || 0,
    radiusMeters: 500,
  });
  // Geofencing is CUT from launch scope, so there are no settings to load and
  // nothing to wait for — start un-loaded when the flag is off.
  // Explicit <boolean>: FEATURES is `as const`, so GEOFENCING_ENABLED has the
  // literal type `false` and an un-annotated useState would infer useState<false>.
  const [loading, setLoading] = useState<boolean>(FEATURES.GEOFENCING_ENABLED);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load existing geofence config from Firebase.
  //
  // Gated on FEATURES.GEOFENCING_ENABLED: `devices/{id}/geofence` is denied by
  // the RTDB rules deployed 2026-08-16, so with the controls hidden this read
  // would only produce a permission-denied warning on every drawer open.
  useEffect(() => {
    if (!FEATURES.GEOFENCING_ENABLED) return;
    if (!isOpen || !seniorId) return;

    setLoading(true);
    const geofenceRef = ref(database, `devices/${seniorId}/geofence`);
    const unsub = onValue(
      geofenceRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setConfig({
            enabled: data.enabled ?? false,
            homeLat: data.homeLat ?? location?.lat ?? 0,
            homeLng: data.homeLng ?? location?.lng ?? 0,
            radiusMeters: data.radiusMeters ?? 500,
          });
        } else if (location) {
          setConfig((prev) => ({ ...prev, homeLat: location.lat, homeLng: location.lng }));
        }
        setLoading(false);
      },
      (err) => {
        console.warn('[GPSZoneDrawer] Failed to load geofence:', err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [isOpen, seniorId, location]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const geofenceRef = ref(database, `devices/${seniorId}/geofence`);
      await set(geofenceRef, {
        enabled: config.enabled,
        homeLat: config.homeLat,
        homeLng: config.homeLng,
        radiusMeters: config.radiusMeters,
        schedule: '24/7',
        updatedAt: Math.floor(Date.now() / 1000),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('[GPSZoneDrawer] Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const hasLocation = !!location && location.lat !== 0 && location.lng !== 0;
  const mapCenter: [number, number] = hasLocation
    ? [location!.lat, location!.lng]
    : config.homeLat !== 0
      ? [config.homeLat, config.homeLng]
      : [45.5019, -73.5674]; // Montreal default

  return (
    <DrawerPanel
      isOpen={isOpen}
      onClose={onClose}
      title={t('dashboard.chips.gpsSafeZone')}
      footer={
        // The only writable state in this drawer was the geofence. With the
        // zone controls hidden there is nothing left to save, so the footer
        // goes with them — a "Save changes" button that saves nothing is the
        // same false-reassurance bug as the zone itself.
        FEATURES.GEOFENCING_ENABLED ? (
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 bg-brand-primary text-white font-semibold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? 'Saving...' : saved ? 'Saved ✓' : 'Save changes'}
          </button>
        ) : undefined
      }
    >
      {/* Map */}
      <div className="rounded-xl overflow-hidden border border-brand-border mb-4" style={{ height: '200px' }}>
        <MapContainer
          center={mapCenter}
          zoom={15}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {hasLocation && <Marker position={[location!.lat, location!.lng]} />}
          {/* Safe-zone radius ring — gated with the controls that set it, so a
              zone saved before launch cannot still be drawn on the map. */}
          {FEATURES.GEOFENCING_ENABLED && config.enabled && config.homeLat !== 0 && (
            <Circle
              center={[config.homeLat, config.homeLng]}
              radius={config.radiusMeters}
              pathOptions={{ color: '#1e5f8a', fillColor: '#1e5f8a', fillOpacity: 0.1 }}
            />
          )}
          <MapAutoCenter lat={mapCenter[0]} lng={mapCenter[1]} />
        </MapContainer>
      </div>

      {/* Last known location */}
      {hasLocation && location && (
        <div className="mb-4 p-3 rounded-xl bg-brand-bg-warm">
          <p className="text-xs text-brand-text-muted mb-0.5">Last known location</p>
          <p className="text-sm font-semibold text-brand-text">{formatLocationTime(location.timestamp)}</p>
          <p className="text-xs text-brand-text-muted mt-1">
            {location.lat.toFixed(5)}, {location.lng.toFixed(5)} · ±{location.accuracy.toFixed(0)}m
          </p>
        </div>
      )}

      {!hasLocation && (
        <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200">
          <p className="text-xs text-amber-900">
            No location yet. {seniorName}'s phone needs to be online with GPS enabled.
          </p>
        </div>
      )}

      {/* Zone-alert checkbox + alert-radius selector.
          =====================================================================
          CUT from v1 (Feature 03 spec: no geofence tables, code or UI in v1 —
          postponed to Wave 1.5), but shipped anyway. Hidden behind the existing
          FEATURES.GEOFENCING_ENABLED flag rather than deleted, so Wave 1.5 is a
          flag flip and not a revert.

          Why this mattered: the Android release build ships
          GEOFENCING_ENABLED=false, so a saved zone enforces nothing, and since
          the 2026-08-16 RTDB rules deploy the write is denied outright. A
          caregiver could draw a zone around her mother's house and be told it
          was "Saved ✓" while nothing on earth would alert her. That is the
          failure mode being removed — not the code.

          Flip in lock-step with the Android flag, never ahead of it. */}
      {FEATURES.GEOFENCING_ENABLED && (
        <>
          {/* Enable toggle */}
          <label className="flex items-center justify-between p-3 rounded-xl bg-white border border-brand-border mb-4 cursor-pointer">
            <div>
              <p className="text-sm font-semibold text-brand-text">Alert when {seniorName} leaves zone</p>
              <p className="text-xs text-brand-text-muted">Get a notification on zone exit</p>
            </div>
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
              className="w-5 h-5 accent-brand-primary"
            />
          </label>

          {/* Radius selector */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-brand-text mb-2">Alert radius</p>
            <div className="grid grid-cols-4 gap-2">
              {RADIUS_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => setConfig({ ...config, radiusMeters: preset.value })}
                  className={`py-2 rounded-lg border-2 text-sm font-semibold transition-colors ${
                    config.radiusMeters === preset.value
                      ? 'border-brand-primary bg-brand-primary/5 text-brand-primary'
                      : 'border-brand-border text-brand-text-muted hover:border-brand-primary/40'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {loading && (
            <div className="mt-4 text-center text-xs text-brand-text-muted">Loading current settings...</div>
          )}
        </>
      )}
    </DrawerPanel>
  );
};

export default GPSZoneDrawer;
